import React, { FC, useMemo, useState } from 'react';
import { useCountries } from '@/api/Countries';
import {
  useCreateLocation,
  useLocations,
  useUpdateLocation,
} from '@/api/Locations';
import { useLocationTypes } from '@/api/LocationTypes';
import { CustomColumns, CustomTable, faEdit, Icon } from '@/ui/components';
import { Location } from '@/types';
import { Button, Space, Switch, Tag } from 'antd';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { LocationPopup } from '../LocationPopup';

export const LocationsDataGrid: FC = () => {
  const { t } = useTranslate();
  const {
    userPermissions: { customers },
    hasGbaAdminAccess,
  } = useAppCustomers();

  const {
    data: locations,
    isFetching: isLocationsLoading,
    refetch,
  } = useLocations();
  const { mutateAsync: createMutate } = useCreateLocation();
  const { mutateAsync: updateMutate } = useUpdateLocation();

  const { data: countries, isFetching: isCountriesLoading } = useCountries();
  const { data: locationTypes, isFetching: isLocationTypesLoading } =
    useLocationTypes();

  const [location, setLocation] = useState<Location>();

  const onSubmit = async (data: Location) => {
    const res = await (data.id ? updateMutate(data) : createMutate(data));

    if (res.success) refetch();

    return res.message?.Message;
  };

  const isLoading =
    isLocationsLoading || isCountriesLoading || isLocationTypesLoading;

  const columns: CustomColumns<Location> = useMemo(
    () => [
      {
        dataIndex: 'desc',
        title: 'Location Description',
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'capacity',
        title: 'Capacity',
        filter: true,
        sortable: true,
      },
      { dataIndex: 'city', title: 'City', filter: true, sortable: true },
      {
        dataIndex: 'postCode',
        title: 'Postcode',
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'locationTypeId',
        title: 'Location Type',
        renderType: 'select',
        render: (_, data) => data.locationType,
        filter: true,
        filterOptions: locationTypes?.map((o) => ({
          value: o.id,
          text: o.name,
        })),
        sortable: true,
      },
      {
        dataIndex: 'countryId',
        title: 'Country Code',
        renderType: 'select',
        render: (_, data) => data.countryCodeIso,
        filter: true,
        filterOptions: countries?.map((o) => ({ value: o.id, text: o.iso })),
        sortable: true,
      },
      {
        dataIndex: 'countryId',
        title: 'County Description',
        renderType: 'select',
        render: (_, data) => data.country,
        filter: true,
        filterOptions: countries?.map((o) => ({ value: o.id, text: o.name })),
        sortable: true,
      },
      {
        dataIndex: 'customers',
        title: 'Customers',
        filter: true,
        onFilter: (value, record) =>
          record.customers.some((o) =>
            o.text?.toLowerCase().includes((value as string).toLowerCase()),
          ),
        render: (_, data) => (
          <Space wrap>
            {data.customers.map((o) => (
              <Tag key={o.value} className="py-1 m-0 text-wrap">
                {o.text}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        dataIndex: 'isActive',
        title: 'Active',
        render: (value) => <Switch checked={value} />,
      },
      {
        dataIndex: 'id',
        render: (_, data) =>
          hasGbaAdminAccess ? (
            <Button onClick={() => setLocation({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>
          ) : null,
        width: 150,
      },
    ],
    [countries, hasGbaAdminAccess, locationTypes, t],
  );

  return (
    <>
      <CustomTable
        dataSource={locations || []}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        onAdd={() => setLocation({} as Location)}
      />

      <LocationPopup
        data={location}
        countries={countries || []}
        locationTypes={locationTypes || []}
        customers={
          customers?.map((o) => ({
            label: o.customerName,
            value: o.customerId,
          })) || []
        }
        onCancel={() => setLocation(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
